import {FC, useState} from 'react';
import {Add} from '@mui/icons-material';
import {Box, Typography} from '@mui/material';
import {Form, Formik, FormikConfig, FormikProps} from 'formik';
import {FormattedMessage, useIntl} from 'react-intl';
import * as Yup from 'yup';
import {companyApi} from 'src/api';
import {useCan} from 'src/permissions/hooks';
import {useAppDispatch} from 'src/app/hooks';
import {AMPInput} from 'src/components/common/AMPInput';
import AMPModal from 'src/components/common/AMPModal';
import {CancelButton} from 'src/components/common/CancelButton';
import {CardList} from 'src/components/common/CardList';
import {ResponsiveTable} from 'src/components/common/ResponsiveTable';
import {SubmitButton} from 'src/components/common/SubmitButton';
import {EmptyTable} from 'src/components/tables/EmptyTable';
import {Table} from 'src/components/tables/Table';
import {TableLayout} from 'src/components/tables/TableLayout';
import {preloaderChangeStatus} from 'src/stores/common/commonSlice';
import {getHelperText} from 'src/utils/forms';
import {isErrorObject} from 'src/utils/helper';
import {NotifyError, NotifySuccess} from 'src/utils/notification';
import {CompanyCredentialItemCard} from './CompanyCredentialItemCard';
import {columnConfigCompanyCredentials} from './CompanyCredentialsTable.tableConfig';
import {CompanyCredentialsTableProps} from './CompanyCredentialsTable.types';
import {
    actionsWrapperSx,
    addButtonSx,
    formSx,
    tableSx,
} from './CompanyCredentialsTable.sx';

interface CredentialFormType extends Record<string, unknown> {
    emailAddress: string;
    firstName: string;
    lastName: string;
}

const credentialInitialValues: CredentialFormType = {
    emailAddress: '',
    firstName: '',
    lastName: '',
};

/**
 * The address is the whole record; the name only labels it.
 *
 * No password field, by design: creating a credential mails an invitation and the person chooses
 * their own. An administrator never knows, and never has to transmit, somebody else's password.
 */
const credentialSchema = Yup.object().shape({
    emailAddress: Yup.string().email('schemaValidEmail').required('schemaRequiredEmail'),
});

export const CompanyCredentialsTable: FC<CompanyCredentialsTableProps> = ({
                                                                             companyId,
                                                                             businessId,
                                                                         }) => {
    const intl = useIntl();
    // The tab opens on the read right; everything that changes the list checks this one.
    const canManage = useCan('companies.manageCredentials');
    const dispatch = useAppDispatch();
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);

    const {data: credentials, refetch} = companyApi.useGetCompanyCredentialsQuery(
        {companyId, businessId},
        {skip: !companyId || !businessId}
    );
    const [addTrigger] = companyApi.useAddCompanyCredentialMutation();
    const [resendTrigger] = companyApi.useResendCompanyCredentialInviteMutation();
    const [deleteTrigger] = companyApi.useDeleteCompanyCredentialMutation();

    const rows = credentials ?? [];

    const handleOpenAddModal = () => {
        setIsAddModalOpen(true);
    };

    const handleCloseAddModal = () => {
        setIsAddModalOpen(false);
    };

    const handleAddCredential: FormikConfig<CredentialFormType>['onSubmit'] = async (values) => {
        try {
            dispatch(preloaderChangeStatus(true));
            await addTrigger({
                companyId,
                businessId,
                data: {
                    emailAddress: values.emailAddress.trim(),
                    firstName: values.firstName.trim() || undefined,
                    lastName: values.lastName.trim() || undefined,
                },
            }).unwrap();
            NotifySuccess(intl.formatMessage({id: 'createdSuccessfully'}));
            handleCloseAddModal();
            // The list is refetched after every write rather than left to the cache: this table is
            // read inside a tab the query was started in, and a row whose status just changed is the
            // only thing anyone came here to see.
            await refetch();
        } catch (error) {
            if (isErrorObject(error)) {
                NotifyError(error.data?.message ?? error.data?.error);
            }
        } finally {
            dispatch(preloaderChangeStatus(false));
        }
    };

    const handleResend = async (credentialId: number) => {
        try {
            dispatch(preloaderChangeStatus(true));
            await resendTrigger({companyId, businessId, credentialId}).unwrap();
            NotifySuccess(intl.formatMessage({id: 'credentialInvitationSent'}));
            await refetch();
        } catch (error) {
            if (isErrorObject(error)) {
                NotifyError(error.data?.message ?? error.data?.error);
            }
        } finally {
            dispatch(preloaderChangeStatus(false));
        }
    };

    const handleDelete = async (credentialId: number) => {
        try {
            dispatch(preloaderChangeStatus(true));
            await deleteTrigger({companyId, businessId, credentialId}).unwrap();
            NotifySuccess(intl.formatMessage({id: 'deletedSuccessfully'}));
            await refetch();
        } catch (error) {
            if (isErrorObject(error)) {
                NotifyError(error.data?.message ?? error.data?.error);
            }
        } finally {
            dispatch(preloaderChangeStatus(false));
        }
    };

    return (
        // Flush inside the relationships card: rows and an add-row footer, no second card.
        <TableLayout
            flush
            hideFooter
            footerPadded={false}
            footer={canManage ? (
                <Box
                    sx={addButtonSx}
                    onClick={handleOpenAddModal}
                >
                    <Add sx={{mr: 1, fontSize: '1.25rem'}}/>
                    <Typography
                        variant="body2"
                        color="primary"
                    >
                        <FormattedMessage id="addCredential"/>
                    </Typography>
                </Box>
            ) : undefined}
        >
            <ResponsiveTable
                responsiveElement={
                    // The grid cannot follow the card's width on a phone, so the rows are the cards
                    // themselves. The empty case keeps the grid's own overlay.
                    rows.length ? (
                        <CardList>
                            {rows.map((credential) => (
                                <CompanyCredentialItemCard
                                    key={credential.id}
                                    credential={credential}
                                    onResend={handleResend}
                                    onDelete={handleDelete}
                                    canManage={canManage}
                                />
                            ))}
                        </CardList>
                    ) : (
                        <EmptyTable/>
                    )
                }
                desktopElement={
                    <Table
                        rows={rows}
                        hideFooter
                        columns={columnConfigCompanyCredentials(handleResend, handleDelete, canManage)}
                        sx={tableSx}
                    />
                }
            />

            <AMPModal
                handleClose={handleCloseAddModal}
                open={isAddModalOpen}
                title={intl.formatMessage({id: 'addCredential'})}
                type="sm"
                fullWidth
                withoutPaddings
            >
                <Formik
                    initialValues={credentialInitialValues}
                    validationSchema={credentialSchema}
                    onSubmit={handleAddCredential}
                >
                    {({
                          errors,
                          handleBlur,
                          handleChange,
                          handleSubmit,
                          isSubmitting,
                          touched,
                          values,
                      }: FormikProps<CredentialFormType>) => (
                        <Form
                            onSubmit={handleSubmit}
                            noValidate
                        >
                            <Box sx={formSx}>
                                <AMPInput
                                    value={values.emailAddress}
                                    type="email"
                                    label="emailAddress"
                                    input="emailAddress"
                                    required
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    hasError={touched.emailAddress && Boolean(errors.emailAddress)}
                                    helperText={getHelperText('emailAddress', touched, errors, intl)}
                                />
                                <AMPInput
                                    value={values.firstName}
                                    type="text"
                                    label="firstName"
                                    input="firstName"
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    hasError={touched.firstName && Boolean(errors.firstName)}
                                    helperText={getHelperText('firstName', touched, errors, intl)}
                                />
                                <AMPInput
                                    value={values.lastName}
                                    type="text"
                                    label="lastName"
                                    input="lastName"
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    hasError={touched.lastName && Boolean(errors.lastName)}
                                    helperText={getHelperText('lastName', touched, errors, intl)}
                                />
                            </Box>
                            <Box sx={actionsWrapperSx}>
                                <CancelButton
                                    text="cancel"
                                    handler={handleCloseAddModal}
                                />
                                <SubmitButton
                                    text="save"
                                    isSubmitting={isSubmitting}
                                />
                            </Box>
                        </Form>
                    )}
                </Formik>
            </AMPModal>
        </TableLayout>
    );
};
